/**
 * LZ Decompress Tool
 *
 * Decompress LZ-string compressed text. Auto-detects compression format.
 */

import LZString from 'lz-string';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

export const LzDecompressTool: ITransformerTool = {
  id: 'lz-decompress',
  name: 'LZ Decompress',
  description: 'Decompress LZ-string compressed text with auto-format detection',
  version: '1.0.0',
  type: 'transformer',
  category: 'Encoders & Decoders',
  keywords: ['lz', 'lz-string', 'decompress', 'decompression', 'lzw'],

  seo: {
    slug: 'lz-string-decompress',
    pageTitle: 'LZ-String Decompress - Free Online Tool | Prism',
    metaDescription: 'Decompress LZ-string compressed data. Auto-detects base64, URI-safe, and UTF-16 formats. Fast, private, runs entirely in your browser.',
    keywords: ['lz-string decompress', 'lz decompress online', 'text decompression', 'lzw decompress', 'decompress string'],
    relatedTools: ['lz-compress', 'base64-decode'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'inputFormat',
      type: 'select',
      label: 'Input Format',
      defaultValue: 'auto',
      options: ['auto', 'base64', 'uri', 'utf16', 'raw'],
      description: 'Format of the compressed input (auto-detect tries all formats)',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const format = String(config.inputFormat ?? 'auto');
    const text = String(input ?? '').trim();
    if (!text) return '';

    try {
      if (format !== 'auto') {
        const result = decompressWithFormat(text, format);
        if (result === null) {
          throw ToolError.validation(`Failed to decompress as ${format} format`);
        }
        return result;
      }

      // Auto-detect: try each format in order of likelihood
      const formats = ['base64', 'uri', 'utf16', 'raw'] as const;
      for (const fmt of formats) {
        try {
          const result = decompressWithFormat(text, fmt);
          if (result !== null && result.length > 0) {
            return result;
          }
        } catch {
          // Try next format
        }
      }

      throw ToolError.validation('Could not decompress input. Try specifying the input format manually.');
    } catch (e) {
      if (e instanceof ToolError) throw e;
      const message = e instanceof Error ? e.message : 'Failed to decompress';
      throw ToolError.validation(message);
    }
  },
};

function decompressWithFormat(input: string, format: string): string | null {
  switch (format) {
    case 'base64':
      return LZString.decompressFromBase64(input);
    case 'uri':
      return LZString.decompressFromEncodedURIComponent(input);
    case 'utf16':
      return LZString.decompressFromUTF16(input);
    case 'raw':
      return LZString.decompress(input);
    default:
      return null;
  }
}
